import { useState, useEffect } from "react"
import { Form } from "antd"

import FormSectionWrapper from "pages/dashboard/people/people/FormSectionWrapper"
import PrevNextButton from "pages/dashboard/people/people/form/PrevNextButton"

import { CustomSelect } from "pages/dashboard/people/people/form"
import { COMMON_ENDPOINTS, commonService } from "services/commonService"
import { COMPANY_ENDPOINTS, companyService } from "services/companyService"
import { IBanksData, ICountry } from "pages/dashboard/people/people/typings"
import { ICurrencies, IKYCData, KYCWalletBody } from "./interface/kyc"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { newArchAxiosInstance } from "utils/request"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import { CustomInput } from "components/new-stuff/form"
import { onQueryError } from "utils/utils"
import { invalidateCompanySetupInfoQueries } from "./utils/invalidateCompanySetupInfoQueries"

export const BankAccountInfoTab = ({
  setActiveKey,
  isActive,
  setUserInfo,

  userInfo,
}: {
  isActive: boolean
  userInfo: KYCWalletBody
  setUserInfo: Function
  setActiveKey: React.Dispatch<React.SetStateAction<string[]>>
  // kyc: IKYCData | undefined
}) => {
  const [form] = Form.useForm()

  const queryClient = useQueryClient()
  const [alpha2Code, setAlpha2Code] = useState<string>("")
  const { isLoading: isLoading, mutate: submit } = useMutation({
    mutationFn: async (body: KYCWalletBody) => {
      const res = await newArchAxiosInstance.post(
        `/admin/company/update_verification`,
        body
      )
      return res.data
    },
    onSuccess: (data) => {
      mixPanelEvent("settings-integration-payment-config-edit-success")
      invalidateCompanySetupInfoQueries(queryClient)
    },
    onError: onQueryError,
  })

  const onFinish = (values: Values) => {
    const {
      phonenumber,
      accountNumber,
      bank,
      country,
      currency,
      full_name,
      bvn_number,
    } = values
    mixPanelEvent("settings-integration-payment-config-edit-init")
    setUserInfo({
      ...userInfo,
      phone: phonenumber,
      bank_code:
        bank == kyc?.verification?.bank_name
          ? kyc?.verification?.bank_code
          : userInfo?.bank_code,
      currency_id:
        currency == kyc?.verification?.currency_code
          ? String(kyc?.verification?.currency_id)
          : String(userInfo?.currency_id),
      bank_country_code:
        country == kyc?.verification?.bank_country
          ? kyc?.verification?.bank_country_code
          : userInfo?.bank_country_code,
      account_number: accountNumber,
    })

    submit({
      phone: phonenumber,
      bank_code:
        bank == kyc?.verification?.bank_name
          ? kyc?.verification?.bank_code
          : userInfo?.bank_code,
      currency_id:
        currency == kyc?.verification?.currency_code
          ? String(kyc?.verification?.currency_id)
          : String(userInfo?.currency_id),
      bank_country_code:
        country == kyc?.verification?.bank_country
          ? kyc?.verification?.bank_country_code
          : userInfo?.bank_country_code,
      account_number: String(accountNumber),
    })

    {
      userInfo?.bank_country_code === "NG"
        ? setActiveKey(["2"])
        : setActiveKey(["3"])
    }
  }
  const { isLoading: isCountriesLoading, data: countriesRes } = useQuery<
    IBaseRes<ICountry[]>
  >({
    queryKey: [COMMON_ENDPOINTS.GET_COUNTRIES],
    queryFn: () => commonService.getCountries(),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })
  const countries = countriesRes?.data
  const { isLoading: isCurrencyLoading, data: currenciesRes } = useQuery<
    IBaseRes<ICurrencies[]>
  >({
    queryKey: [COMMON_ENDPOINTS.GET_CURRENCIES, { forpayment: true }],
    queryFn: () => commonService.getCurrencies({ forpayment: true }),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })
  const currencies = currenciesRes?.data
  const {
    data: banksRes,
    isLoading: isBanksDataLoading,
    refetch,
  } = useQuery<IBaseRes<IBanksData[]>>({
    queryKey: [COMMON_ENDPOINTS.GET_BANKS_BY_COUNTRY, alpha2Code],
    queryFn: () => commonService.getBanksByCountry(alpha2Code),
    enabled: !!alpha2Code,
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
    keepPreviousData: true,
  })
  const banks = banksRes?.data

  const { isLoading: isGetKYCLoading, data: kycRes } = useQuery<
    IBaseRes<IKYCData>
  >({
    queryKey: [COMPANY_ENDPOINTS.VIEW_SETTINGS, "verify"],
    queryFn: () => companyService.viewSettings("verify"),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
    onSuccess: (res) => {
      setAlpha2Code(res?.data?.verification?.country_code || "")
    },
  })
  const kyc = kycRes?.data

  return (
    <FormSectionWrapper
      name="Bank Account Information"
      title="Bank Account Information"
      tooltip="This will be the only account used for all company wallet withdrawals. You can always update it anytime from your settings"
      subtitle={
        <span>
          Enter the bank details where your withdrawals will be deposited.
        </span>
      }
      isActive={isActive}
      initialValues={{
        phonenumber: kyc?.verification?.phone,
        country: kyc?.verification?.bank_country,
        bank: kyc?.verification?.bank_name,
        currency: kyc?.verification?.currency_code,
        accountNumber: kyc?.verification?.account_number,
      }}
      layout="vertical"
      form={form}
      onFinish={onFinish}
      style={{ maxWidth: 1024 }}
      autoComplete="off"
      footerBtns={<PrevNextButton showPrevButton={false} />}
    >
      <div className="flex flex-col md:grid grid-cols-2 gap-x-10 px-4 md:px-6 xl:px-[50px]">
        <CustomInput
          label="Phone number"
          name="phonenumber"
          required
          type="number"
          maxLength={15}
        />
        <CustomSelect
          label="Country"
          name="country"
          required
          onChange={(selected) => {
            const country = countries?.find(
              (country) => country.description === selected
            )
            setUserInfo({
              ...userInfo,
              bank_country_code: country?.alpha2Code,
            })
            form.setFieldsValue({
              currency: undefined,
              bank: undefined,
            })
            setAlpha2Code(country?.alpha2Code || "")
            refetch()
          }}
          isLoading={isCountriesLoading}
          options={
            isCountriesLoading
              ? []
              : countries?.map((state) => ({
                  label: state.description,
                  value: state.description,
                }))
          }
          placeholder="Please select"
        />
        {alpha2Code !== "" && (
          <CustomSelect
            label="Bank"
            name="bank"
            required
            isLoading={isBanksDataLoading}
            onChange={(selected) => {
              const bank = banks?.find((bank) => bank.description === selected)
              setUserInfo({
                ...userInfo,
                bank_code: bank?.code,
              })
            }}
            options={
              isBanksDataLoading
                ? []
                : banks?.map((bank) => ({
                    label: bank.description,
                    value: bank.description,
                  }))
            }
            placeholder=""
          />
        )}
        <CustomInput
          label="Account number"
          name="accountNumber"
          required
        />

        <CustomSelect
          label="Currency"
          name="currency"
          required
          onChange={(selected) => {
            const currency = currencies?.find(
              (currency) => currency.code === selected
            )

            setUserInfo({
              ...userInfo,
              currency_id: currency?.currencyId,
            })
            setTimeout(() => {
              // form.setFieldsValue({
              //   state: "",
              // })
              refetch()
            }, 100)
          }}
          isLoading={isCurrencyLoading}
          options={
            isCurrencyLoading
              ? []
              : currencies?.map((state) => ({
                  label: state.code,
                  value: state.code,
                }))
          }
          placeholder="Please select"
        />
      </div>
    </FormSectionWrapper>
  )
}
interface Values {
  phonenumber: string
  country: string
  bank: string
  currency: string
  accountNumber: boolean
  full_name: string
  bvn_number: string
}
